import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users as UsersIcon, LogOut, Send, Search, Shield, Plus, Pencil, Trash2, KeyRound } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const MODULE_LABELS = {
  dashboard: 'Dashboard',
  tenants: 'Tenants',
  plans: 'Plan Builder',
  addons: 'Add-ons',
  usage: 'Consumo & Costos',
  ai: 'IA',
  users: 'Usuarios',
  tickets: 'Soporte',
  audit: 'Auditoría',
  cobranza: 'Cobranza, socios y comisiones',
  verticals: 'Verticales',
  infrastructure: 'Infraestructura',
  // Incluye Cobros (tenant), Tarifario y Mensajes del sistema en backend.
  settings: 'Configuración (cobros tenant, tarifario, mensajes, MFA)',
  roadmap: 'Tareas sugeridas',
};

/**
 * Las personas que entran al portal del socio.
 *
 * Va como componente exportado y no como JSX dentro de la pestaña porque las
 * pestañas de Radix solo montan la activa: dentro, el arnés de render nunca la
 * vería y la daría por buena sin haberla mirado.
 */
export function TablaUsuariosDeSocio({ usuarios, onCambiarClave }) {
  return (
          <div className="border border-[#E2E8F0] rounded-xl bg-white p-4" data-testid="tabla-usuarios-socio">
            <p className="text-sm text-[#64748B] mb-3">
              Las personas que entran al <b>portal del socio</b>. No son de la
              plataforma ni de ningún negocio: no ven la operación de sus
              clientes y no tienen permisos aquí dentro. Esta pestaña existe
              para lo que se necesita de verdad — que si un socio pierde su
              contraseña puedas devolvérsela sin que se quede fuera.
            </p>
            {usuarios.length === 0 ? (
              <p className="text-sm text-[#94A3B8] py-4 text-center">
                Todavía no hay usuarios de socio. Al dar de alta un socio con correo
                se crea la credencial del portal; si faltó, en Super Admin › Socios
                se puede crear después (POST credencial) o al re-alta.
              </p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nombre</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Email</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Socio</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Último login</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Estado</th>
                    <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    <tr key={u.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA]">
                      <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-[#64748B]">{u.email}</td>
                      <td className="px-4 py-3 text-sm text-[#0F172A]">
                        {u.partner_name || <span className="text-amber-600">sin socio asignado</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#94A3B8]">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString('es-MX') : 'Nunca'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium border ${u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {/* Mismo modal que el resto: exige motivo escrito, cierra
                            sus sesiones y le avisa por correo. */}
                        <Button variant="ghost" size="sm"
                          onClick={() => onCambiarClave(u)}
                          className="text-[#64748B] hover:text-amber-700"
                          data-testid={`reset-socio-${u.id}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-1" />
                          <span className="text-xs">Cambiar contraseña</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
  );
}


export default function Users() {
  const { isPlatformOwner, isPlatformOwnerRoot } = useAuth();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [superUsers, setSuperUsers] = useState([]);
  const [sociosUsers, setSociosUsers] = useState([]);
  const [modules, setModules] = useState(Object.keys(MODULE_LABELS));
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [forcingLogout, setForcingLogout] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resending, setResending] = useState(null);
  const [showSuperForm, setShowSuperForm] = useState(false);
  const [editingSuper, setEditingSuper] = useState(null);
  const [savingSuper, setSavingSuper] = useState(false);
  const [superForm, setSuperForm] = useState({
    name: '', email: '', password: '', level: 'admin', platform_module_grants: [], is_active: true,
  });

  const fetchUsers = async (searchTerm = search, active = activeFilter) => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (searchTerm) params.search = searchTerm;
      if (active !== 'all') params.is_active = active === 'active';
      const { data } = await api.get('/admin/users/all', { params });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const fetchInvitations = async () => {
    try {
      const { data } = await api.get('/admin/invitations/pending');
      setInvitations(data);
    } catch (e) { /* best-effort */ }
  };

  const fetchSuperUsers = async () => {
    if (!isPlatformOwner) return;
    try {
      const { data } = await api.get('/admin/super-users');
      setSuperUsers(data.users || []);
      if (data.modules?.length) setModules(data.modules);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Los usuarios de SOCIO. Van en su propia pestaña y no dentro de «Super
  // Users» a propósito: ahí viven el personal interno con permisos sobre toda
  // la plataforma, y un socio es un tercero externo. Mezclarlos lo haría
  // parecer interno, y el formulario de esa pestaña reparte permisos de
  // plataforma — justo lo que un socio nunca debe tener.
  const fetchSocios = async () => {
    if (!isPlatformOwner) return;
    try {
      const { data } = await api.get('/admin/users/all?user_type=partner&limit=200');
      setSociosUsers(Array.isArray(data) ? data : []);
    } catch (e) { /* la pantalla sirve igual sin esta pestaña */ }
  };

  useEffect(() => { fetchUsers(); fetchInvitations(); fetchSuperUsers(); fetchSocios(); }, [isPlatformOwner]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(search, activeFilter);
  };

  // S125 — restablecer contraseña. Se pide en un formulario, no en un
  // window.prompt: hay que capturar contraseña Y motivo, y el motivo es lo
  // que deja rastro de POR QUE se hizo.
  const [restableciendo, setRestableciendo] = useState(null);
  const [nuevaClave, setNuevaClave] = useState('');
  const [motivoClave, setMotivoClave] = useState('');
  const [guardandoClave, setGuardandoClave] = useState(false);

  const handleResetPassword = async () => {
    if (nuevaClave.length < 10) { toast.error('La contraseña necesita al menos 10 caracteres.'); return; }
    if (motivoClave.trim().length < 5) { toast.error('Escribe por qué la estás cambiando — queda en la bitácora.'); return; }
    setGuardandoClave(true);
    try {
      const { data } = await api.post(`/admin/users/${restableciendo.id}/reset-password`,
        { new_password: nuevaClave, reason: motivoClave.trim() });
      toast.success(data.mensaje);
      setRestableciendo(null); setNuevaClave(''); setMotivoClave('');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGuardandoClave(false); }
  };

  const handleForceLogout = async (user) => {
    if (!window.confirm(`¿Cerrar sesión de ${user.name} (${user.email}) en todos sus dispositivos ahora?`)) return;
    setForcingLogout(user.id);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/force-logout`);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setForcingLogout(null); }
  };

  const handleDeleteUser = async (u) => {
    if (u.platform_role === 'platform_owner_root') {
      toast.error('No se puede eliminar al Owner raíz');
      return;
    }
    if (!window.confirm(`¿ELIMINAR permanentemente a ${u.name} (${u.email})?\n\nEsta acción no se puede deshacer.`)) return;
    setDeletingUser(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(data.message || 'Usuario eliminado');
      fetchUsers();
      fetchSuperUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeletingUser(null); }
  };

  const handleResend = async (invitation) => {
    setResending(invitation.id);
    try {
      const { data } = await api.post(`/admin/invitations/${invitation.id}/resend`);
      toast.success(data.message);
      fetchInvitations();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(null); }
  };

  const openCreateSuper = () => {
    setEditingSuper(null);
    setSuperForm({
      name: '', email: '', password: '',
      level: isPlatformOwnerRoot ? 'admin' : 'admin',
      platform_module_grants: ['dashboard'],
      is_active: true,
    });
    setShowSuperForm(true);
  };

  const openEditSuper = (u) => {
    setEditingSuper(u);
    setSuperForm({
      name: u.name,
      email: u.email,
      password: '',
      level: u.level === 'owner' || u.platform_role === 'super_admin_platform' || u.platform_role === 'platform_owner_root' ? 'owner' : 'admin',
      platform_module_grants: u.platform_module_grants || [],
      is_active: u.is_active,
    });
    setShowSuperForm(true);
  };

  const toggleGrant = (key) => {
    setSuperForm((f) => {
      const has = f.platform_module_grants.includes(key);
      return {
        ...f,
        platform_module_grants: has
          ? f.platform_module_grants.filter((k) => k !== key)
          : [...f.platform_module_grants, key],
      };
    });
  };

  const handleSaveSuper = async () => {
    if (!superForm.name.trim() || (!editingSuper && !superForm.email.trim())) {
      toast.error('Nombre y correo son requeridos');
      return;
    }
    if (!editingSuper && superForm.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (superForm.level === 'admin' && superForm.platform_module_grants.length === 0) {
      toast.error('Asigna al menos un módulo al administrador');
      return;
    }
    setSavingSuper(true);
    try {
      if (editingSuper) {
        const payload = {
          name: superForm.name,
          is_active: superForm.is_active,
          level: superForm.level,
        };
        if (superForm.password) payload.password = superForm.password;
        if (superForm.level === 'admin') payload.platform_module_grants = superForm.platform_module_grants;
        await api.patch(`/admin/super-users/${editingSuper.id}`, payload);
        toast.success('Super usuario actualizado');
      } else {
        await api.post('/admin/super-users', {
          name: superForm.name,
          email: superForm.email,
          password: superForm.password,
          level: superForm.level,
          platform_module_grants: superForm.level === 'admin' ? superForm.platform_module_grants : [],
          is_active: superForm.is_active,
        });
        toast.success('Super usuario creado');
      }
      setShowSuperForm(false);
      fetchSuperUsers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingSuper(false); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Usuarios</h1>
        <p className="text-sm text-[#64748B] mt-1">Usuarios de tenants y equipo de Super Admin</p>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="bg-white border border-[#E2E8F0] rounded-lg p-1 flex flex-wrap h-auto">
          <TabsTrigger value="users" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-users">
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="invitations" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-invitations">
            Invitaciones Pendientes {invitations.length > 0 && `(${invitations.length})`}
          </TabsTrigger>
          {isPlatformOwner && (
            <TabsTrigger value="super-users" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-super-users">
              <Shield className="w-3.5 h-3.5 mr-1.5" /> Super Users
            </TabsTrigger>
          )}
          {isPlatformOwner && (
            <TabsTrigger value="socios" className="text-sm data-[state=active]:bg-[#0F172A] data-[state=active]:text-white rounded-md" data-testid="tab-usuarios-socio">
              Socios {sociosUsers.length > 0 && `(${sociosUsers.length})`}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="socios">
          <TablaUsuariosDeSocio
            usuarios={sociosUsers}
            onCambiarClave={(u) => { setRestableciendo(u); setNuevaClave(''); setMotivoClave(''); }} />
        </TabsContent>

        <TabsContent value="users">
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o correo..."
                className="pl-9 bg-white border-[#E2E8F0] text-sm" data-testid="user-search-input" />
            </div>
            <Select value={activeFilter} onValueChange={v => { setActiveFilter(v); fetchUsers(search, v); }}>
              <SelectTrigger className="w-40 bg-white border-[#E2E8F0] text-sm" data-testid="user-active-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inactivos</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline">Buscar</Button>
          </form>

          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>
            ) : users.length === 0 ? (
              <div className="text-center py-16 text-[#94A3B8]">
                <UsersIcon className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
                <p className="text-sm">Sin resultados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]" data-testid="users-table">
                  <thead>
                    <tr className="bg-[#F8F9FA]">
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nombre</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Email</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Tenant</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Rol</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Último login</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Estado</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA]" data-testid={`user-row-${u.id}`}>
                        <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{u.name}</td>
                        <td className="px-4 py-3 text-sm text-[#64748B]">{u.email}</td>
                        {/* Un socio no es «Plataforma»: decirlo así lo disfraza
                            de cuenta interna y alguien podría borrarlo o darle
                            permisos que no le tocan. */}
                        <td className="px-4 py-3 text-sm text-[#64748B]">
                          {u.tenant_name
                            || (u.user_type === 'partner'
                                ? <span className="text-[#0F172A]">Socio: {u.partner_name || 'sin asignar'}</span>
                                : <span className="text-[#CBD5E1]">Plataforma</span>)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0] capitalize">
                            {(u.tenant_role || u.platform_role || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#94A3B8]">{u.last_login_at ? new Date(u.last_login_at).toLocaleString('es-MX') : 'Nunca'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium border ${u.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                            {u.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleForceLogout(u)} disabled={forcingLogout === u.id}
                              className="text-[#64748B] hover:text-red-600" data-testid={`force-logout-${u.id}`}>
                              <LogOut className="w-3.5 h-3.5 mr-1" />
                              <span className="text-xs">{forcingLogout === u.id ? 'Cerrando...' : 'Cerrar sesión'}</span>
                            </Button>
                            {isPlatformOwner && (
                              <Button variant="ghost" size="sm"
                                onClick={() => { setRestableciendo(u); setNuevaClave(''); setMotivoClave(''); }}
                                className="text-[#64748B] hover:text-amber-700"
                                data-testid={`reset-password-${u.id}`}>
                                <KeyRound className="w-3.5 h-3.5 mr-1" />
                                <span className="text-xs">Cambiar contraseña</span>
                              </Button>
                            )}
                            {isPlatformOwner && u.platform_role !== 'platform_owner_root' && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u)} disabled={deletingUser === u.id}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                <span className="text-xs">{deletingUser === u.id ? 'Eliminando...' : 'Eliminar'}</span>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invitations">
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            {invitations.length === 0 ? (
              <div className="text-center py-16 text-[#94A3B8]">
                <Send className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
                <p className="text-sm">Sin invitaciones pendientes</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]" data-testid="invitations-table">
                  <thead>
                    <tr className="bg-[#F8F9FA]">
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Email</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Tenant</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Expira</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map(inv => (
                      <tr key={inv.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA]" data-testid={`invitation-row-${inv.id}`}>
                        <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{inv.email}</td>
                        <td className="px-4 py-3 text-sm text-[#64748B]">{inv.tenant_name}</td>
                        <td className="px-4 py-3 text-xs text-[#94A3B8]">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString('es-MX') : ''}</td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm" onClick={() => handleResend(inv)} disabled={resending === inv.id}
                            className="text-[#64748B] hover:text-[#0F172A]" data-testid={`resend-invitation-${inv.id}`}>
                            <Send className="w-3.5 h-3.5 mr-1" />
                            <span className="text-xs">{resending === inv.id ? 'Enviando...' : 'Reenviar'}</span>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {isPlatformOwner && (
          <TabsContent value="super-users" data-testid="super-users-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-semibold text-[#0F172A]">Equipo Super Admin</h2>
                <p className="text-[11px] text-[#94A3B8]">
                  Solo Owners. El Owner raíz crea otros Owners; cualquier Owner crea administradores y define qué módulos del panel pueden ver.
                </p>
              </div>
              <Button size="sm" onClick={openCreateSuper} className="bg-[#0F172A] text-white" data-testid="create-super-user-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo Super User
              </Button>
            </div>

            {showSuperForm && (
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-4 space-y-4" data-testid="super-user-form">
                <h3 className="text-sm font-semibold text-[#0F172A]">{editingSuper ? 'Editar Super User' : 'Crear Super User'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#64748B] mb-1 block">Nombre</label>
                    <Input value={superForm.name} onChange={e => setSuperForm(f => ({ ...f, name: e.target.value }))} className="text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] mb-1 block">Correo</label>
                    <Input type="email" disabled={!!editingSuper} value={superForm.email}
                      onChange={e => setSuperForm(f => ({ ...f, email: e.target.value }))} className="text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] mb-1 block">{editingSuper ? 'Nueva contraseña (opcional)' : 'Contraseña'}</label>
                    <Input type="password" value={superForm.password}
                      onChange={e => setSuperForm(f => ({ ...f, password: e.target.value }))} className="text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] mb-1 block">Nivel</label>
                    <select className="w-full h-10 px-3 text-sm border border-[#E2E8F0] rounded-md"
                      value={superForm.level}
                      disabled={editingSuper?.is_root}
                      onChange={e => setSuperForm(f => ({ ...f, level: e.target.value }))}>
                      <option value="admin">Administrador (módulos limitados)</option>
                      {isPlatformOwnerRoot && <option value="owner">Owner (acceso total)</option>}
                    </select>
                  </div>
                </div>
                {superForm.level === 'admin' && (
                  <div>
                    <label className="text-xs text-[#64748B] mb-2 block">Módulos que puede ver / administrar</label>
                    <div className="flex flex-wrap gap-2">
                      {modules.map((key) => (
                        <label key={key} className={`text-xs px-2.5 py-1.5 rounded-full border cursor-pointer transition-all ${
                          superForm.platform_module_grants.includes(key)
                            ? 'bg-[#0F172A] text-white border-[#0F172A]'
                            : 'border-[#E2E8F0] text-[#64748B]'
                        }`}>
                          <input type="checkbox" className="sr-only" checked={superForm.platform_module_grants.includes(key)}
                            onChange={() => toggleGrant(key)} />
                          {MODULE_LABELS[key] || key}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={superForm.is_active}
                    onChange={e => setSuperForm(f => ({ ...f, is_active: e.target.checked }))} />
                  Activo
                </label>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowSuperForm(false)}>Cancelar</Button>
                  <Button size="sm" onClick={handleSaveSuper} disabled={savingSuper} className="bg-[#0F172A] text-white">
                    {savingSuper ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </div>
            )}

            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
              {superUsers.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-10">Sin super usuarios listados</p>
              ) : (
                <table className="w-full min-w-[640px]" data-testid="super-users-table">
                  <thead>
                    <tr className="bg-[#F8F9FA]">
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nombre</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Email</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Nivel</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Módulos</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Estado</th>
                      <th className="text-left text-xs uppercase tracking-widest text-[#64748B] font-semibold px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {superUsers.map((u) => (
                      <tr key={u.id} className="border-t border-[#E2E8F0]">
                        <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{u.name}</td>
                        <td className="px-4 py-3 text-sm text-[#64748B]">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            u.is_root || u.level === 'owner' ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-700'
                          }`}>
                            {u.is_root ? 'Owner raíz' : u.level === 'owner' ? 'Owner' : 'Administrador'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-[#64748B] max-w-xs">
                          {u.level === 'owner' || u.is_root
                            ? 'Todos'
                            : (u.platform_module_grants || []).map((k) => MODULE_LABELS[k] || k).join(', ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs">{u.is_active ? 'Activo' : 'Inactivo'}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {!u.is_root && (
                              <Button variant="ghost" size="sm" onClick={() => openEditSuper(u)}>
                                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                              </Button>
                            )}
                            {!u.is_root && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u)} disabled={deletingUser === u.id}
                                className="text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* S125 — restablecer la contraseña de cualquier usuario. Existe porque
          cuando alguien cambia de correo, pierde el anterior y olvida su
          contraseña, el enlace de recuperación llega a un buzón muerto y esa
          cuenta se pierde para siempre. */}
      {restableciendo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
             data-testid="modal-reset-password">
          <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3">
            <div>
              <h3 className="font-semibold">Cambiar la contraseña de {restableciendo.name}</h3>
              <p className="text-xs text-[#64748B] mt-0.5">{restableciendo.email}</p>
            </div>
            <div className="rounded bg-amber-50 border border-amber-200 p-2 text-xs">
              Al guardar se cerrarán todas sus sesiones y se le avisará por correo.
              Queda registrado en la bitácora quién lo hizo y por qué.
            </div>
            <div>
              <label className="text-xs">Contraseña nueva (mínimo 10 caracteres)</label>
              <Input type="text" value={nuevaClave} autoComplete="off"
                onChange={e => setNuevaClave(e.target.value)} />
            </div>
            <div>
              <label className="text-xs">¿Por qué la estás cambiando?</label>
              <Input value={motivoClave} placeholder="Perdió acceso a su correo anterior"
                onChange={e => setMotivoClave(e.target.value)} />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="ghost" onClick={() => setRestableciendo(null)}>Cancelar</Button>
              <Button onClick={handleResetPassword} disabled={guardandoClave}>
                {guardandoClave ? 'Guardando…' : 'Cambiar contraseña'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
